import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { initiateLoginRedirect } from '@/lib/oauth';
import { useAuth } from '@/contexts/AuthContext';

const USE_DIRECT_AUTH = import.meta.env.VITE_USE_DIRECT_AUTH === 'true';

export default function LoginFull() {
  const { signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If OAuth is enabled, redirect to OptiPrime
  useEffect(() => {
    if (!USE_DIRECT_AUTH) {
      // Check for prompt params from marketing website handover
      const prompt = searchParams.get('prompt');
      const autoSubmit = searchParams.get('autoSubmit');

      let returnUrl: string | undefined;

      if (prompt) {
        // Build return URL with prompt params to pass through OAuth flow
        returnUrl = `/dashboard?prompt=${encodeURIComponent(prompt)}${autoSubmit ? `&autoSubmit=${autoSubmit}` : ''}`;
      } else {
        // Fallback to return_url from query params (set by ProtectedRoute)
        returnUrl = searchParams.get('return_url') || undefined;
      }

      initiateLoginRedirect(returnUrl);
    }
  }, [searchParams]);

  const handleDirectLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const returnUrl = searchParams.get('return_url') || undefined;
      await signIn(email, password, returnUrl);
      // signIn from AuthContext will handle redirect to return_url or /dashboard
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Direct auth form for local dev
  if (USE_DIRECT_AUTH) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-md">
          <div className="h-12 w-12 rounded-xl gradient-purple flex items-center justify-center mx-auto mb-6">
            <Monitor className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold gradient-text mb-2 text-center">Welcome Back</h1>
          <p className="text-muted-foreground text-center mb-8">Local Development Mode</p>

          <form onSubmit={handleDirectLogin} className="space-y-4 bg-card p-8 rounded-lg border">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <p className="text-sm text-center text-muted-foreground">
              Don't have an account?{' '}
              <a href="/auth/signup" className="text-purple-600 hover:underline">
                Sign up
              </a>
            </p>
          </form>
        </div>
      </div>
    );
  }

  // OAuth redirect (production) - blank screen, redirect is near-instant
  return (
    <div className="min-h-screen bg-white" />
  );
}